import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, Link, useNavigate, useLocation } from 'react-router-dom';
import axiosInstance from '../lib/axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  CheckCircle,
  Package,
  Mail,
  ArrowRight,
  Download,
  Truck,
  Home,
  Printer,
  Phone,
  MapPin
} from 'lucide-react';
import { formatPrice, formatDateTime } from '../lib/utils';
import LoadingSpinner from '../components/LoadingSpinner';
import { toast } from 'sonner';

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Differentiate between session_id (online payment) and order_id (COD)
  const sessionId = searchParams.get('session_id');
  const orderIdFromUrl = searchParams.get('order_id');
  
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pollingCount, setPollingCount] = useState(0);
  const [redirectCountdown, setRedirectCountdown] = useState(10);
  const receiptRef = useRef(null);
  
  // Get order data from navigation state (for COD orders)
  // Also use order_id from URL as COD order ID
  const codOrderId = location.state?.orderId || orderIdFromUrl;
  const orderFromState = location.state?.order;

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Fetch order details by ID
  const fetchOrderDetails = async (orderId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axiosInstance.get(`${API_URL}/api/orders/${orderId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setOrder(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching order:', error);
      // If we have order data from state, use that
      if (orderFromState) {
        setOrder(orderFromState);
      }
      return null;
    }
  };

  const pollPaymentStatus = async () => {
    const maxAttempts = 10;
    const pollInterval = 2000;
    
    // Use order_id from URL if available (for Cashfree redirect)
    const orderIdToVerify = orderIdFromUrl || sessionId;

    const checkStatus = async (attempt = 0) => {
      try {
        console.log(`Verifying payment for order: ${orderIdToVerify}, attempt ${attempt + 1}`);
        const response = await axiosInstance.get(`${API_URL}/api/payment-status/${orderIdToVerify}`);
        const status = response.data;
        
        console.log('Payment verification response:', status);
        setPaymentStatus(status);
        
        if (status.payment_status === 'paid') {
          setLoading(false);
          // Fetch full order details
          await fetchOrderDetails(status.order_id || orderIdToVerify);
          toast.success('Payment confirmed!');
          return;
        }
        
        if (attempt < maxAttempts && status.payment_status !== 'failed') {
          setPollingCount(attempt + 1);
          setTimeout(() => checkStatus(attempt + 1), pollInterval);
        } else {
          setLoading(false);
          // Even if pending, fetch order details to show something
          await fetchOrderDetails(orderIdToVerify);
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        if (attempt < maxAttempts) {
          setTimeout(() => checkStatus(attempt + 1), pollInterval);
        } else {
          setLoading(false);
          // Try to at least show the order
          await fetchOrderDetails(orderIdToVerify);
          toast.error('Could not verify payment status. Please check your orders.');
        }
      }
    };

    checkStatus();
  };

  useEffect(() => {
    const initializePage = async () => {
      // If we have order_id from URL (Cashfree redirect), verify payment status
      if (orderIdFromUrl) {
        console.log('Verifying Cashfree payment for order:', orderIdFromUrl);
        pollPaymentStatus();
      } else if (sessionId) {
        // Legacy support for session_id
        pollPaymentStatus();
      } else if (codOrderId) {
        // COD order - fetch the actual order details
        await fetchOrderDetails(codOrderId);
        setPaymentStatus({ 
          payment_status: 'cod', 
          order_id: codOrderId 
        });
        setLoading(false);
      } else if (orderFromState) {
        // Use order from navigation state
        setOrder(orderFromState);
        setPaymentStatus({ 
          payment_status: orderFromState.payment_method === 'COD' ? 'cod' : 'paid',
          order_id: orderFromState.id || orderFromState._id
        });
        setLoading(false);
      } else {
        setLoading(false);
      }
    };
    
    initializePage();
  }, [sessionId, codOrderId, orderFromState, orderIdFromUrl, API_URL]);

  // Countdown timer
  useEffect(() => {
    if (!loading && (paymentStatus?.payment_status === 'paid' || paymentStatus?.payment_status === 'cod' || codOrderId)) {
      const timer = setInterval(() => {
        setRedirectCountdown(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            navigate('/');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      return () => clearInterval(timer);
    }
  }, [loading, paymentStatus, codOrderId, navigate]);

  // Print receipt function
  const handlePrintReceipt = () => {
    const printContent = receiptRef.current;
    if (!printContent) return;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Order Receipt - NammaHut</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; max-width: 400px; margin: 0 auto; }
            .header { text-align: center; border-bottom: 2px solid #16a34a; padding-bottom: 15px; margin-bottom: 20px; }
            .logo { font-size: 24px; font-weight: bold; color: #16a34a; }
            .tagline { font-size: 12px; color: #666; }
            .order-id { font-size: 14px; margin: 10px 0; }
            .section { margin: 15px 0; padding: 10px 0; border-bottom: 1px dashed #ddd; }
            .section-title { font-weight: bold; margin-bottom: 8px; }
            .row { display: flex; justify-content: space-between; margin: 5px 0; }
            .item { padding: 8px 0; border-bottom: 1px solid #eee; }
            .item-name { font-weight: 500; }
            .item-qty { color: #666; font-size: 12px; }
            .total { font-size: 18px; font-weight: bold; color: #16a34a; }
            .badge { display: inline-block; padding: 4px 10px; border-radius: 20px; font-size: 12px; }
            .badge-cod { background: #fef3c7; color: #92400e; }
            .badge-paid { background: #d1fae5; color: #065f46; }
            .footer { text-align: center; margin-top: 20px; font-size: 12px; color: #666; }
            @media print { body { padding: 10px; } }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="logo">NammaHut</div>
            <div class="tagline">Traditional Indian Marketplace</div>
            <div class="order-id">Order #${(order?.id || codOrderId || '').slice(-8).toUpperCase()}</div>
            <div style="font-size: 12px; color: #666;">${new Date().toLocaleDateString('en-IN', { dateStyle: 'long' })}</div>
          </div>
          
          ${order?.items?.length > 0 ? `
            <div class="section">
              <div class="section-title">Order Items</div>
              ${order.items.map(item => `
                <div class="item">
                  <div class="item-name">${item.name}</div>
                  <div class="row">
                    <span class="item-qty">Qty: ${item.quantity}</span>
                    <span>${formatPrice(item.price * item.quantity)}</span>
                  </div>
                </div>
              `).join('')}
            </div>
          ` : ''}
          
          <div class="section">
            <div class="row">
              <span>Subtotal</span>
              <span>${formatPrice(order?.subtotal || 0)}</span>
            </div>
            <div class="row">
              <span>Shipping</span>
              <span>${formatPrice(order?.shipping_cost || 0)}</span>
            </div>
            <div class="row total">
              <span>Total</span>
              <span>${formatPrice(order?.total || order?.subtotal || 0)}</span>
            </div>
          </div>
          
          <div class="section">
            <div class="row">
              <span>Payment Method</span>
              <span class="badge ${order?.payment_method === 'COD' ? 'badge-cod' : 'badge-paid'}">
                ${order?.payment_method === 'COD' ? 'Cash on Delivery' : 'Paid Online'}
              </span>
            </div>
          </div>
          
          ${order?.shipping_address ? `
            <div class="section">
              <div class="section-title">Shipping Address</div>
              <div>${order.shipping_address.street || ''}</div>
              <div>${order.shipping_address.city || ''}, ${order.shipping_address.state || ''}</div>
              <div>${order.shipping_address.postal_code || ''}</div>
            </div>
          ` : ''}
          
          <div class="footer">
            <p>Thank you for shopping with NammaHut!</p>
            <p>Questions? Contact us at support@nammahut.com</p>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <LoadingSpinner size="lg" className="mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {codOrderId ? 'Loading order details...' : 'Confirming your payment...'}
            </h2>
            <p className="text-gray-600 mb-4">
              Please wait while we verify your order. This usually takes a few seconds.
            </p>
            {pollingCount > 0 && (
              <p className="text-sm text-gray-500">
                Attempt {pollingCount} of 10
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // No order found
  if (!sessionId && !codOrderId && !orderFromState) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Order Information Not Found
            </h2>
            <p className="text-gray-600 mb-6">
              We couldn't find your order information. Please check your email for confirmation or contact support.
            </p>
            <div className="space-y-3">
              <Link to="/orders" className="block">
                <Button className="w-full">View Orders</Button>
              </Link>
              <Link to="/" className="block">
                <Button variant="outline" className="w-full">Back to Home</Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Determine payment status
  const isCOD = order?.payment_method === 'COD' || paymentStatus?.payment_status === 'cod';
  const isPaymentSuccessful = paymentStatus?.payment_status === 'paid' || isCOD;
  const displayAmount = order?.total || order?.subtotal || (paymentStatus?.amount_total ? paymentStatus.amount_total / 100 : 0);
  const orderId = order?.id || order?._id || codOrderId || sessionId;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Success Header */}
        <div className="text-center mb-12">
          <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 ${
            isPaymentSuccessful ? 'bg-green-100' : 'bg-yellow-100'
          }`}>
            {isPaymentSuccessful ? (
              <CheckCircle className="w-12 h-12 text-green-600" />
            ) : (
              <LoadingSpinner size="lg" />
            )}
          </div>
          
          <h1 className={`text-3xl lg:text-4xl font-bold mb-4 ${
            isPaymentSuccessful ? 'text-gray-900' : 'text-yellow-700'
          }`}>
            {isCOD ? 'Order Confirmed!' : (isPaymentSuccessful ? 'Payment Successful!' : 'Processing Payment...')}
          </h1>
          
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {isCOD ? 
              'Thank you for your order! Please keep the exact amount ready for Cash on Delivery.' :
              (isPaymentSuccessful ? 
                'Thank you for your order! We\'ve received your payment and are processing your order.' :
                'Your payment is being processed. This page will update automatically.'
              )
            }
          </p>
          
          {/* Order ID Display */}
          {orderId && (
            <div className="mt-4 inline-block bg-gray-100 px-4 py-2 rounded-lg">
              <span className="text-sm text-gray-600">Order ID: </span>
              <span className="font-mono font-bold text-gray-900">#{orderId.slice(-8).toUpperCase()}</span>
            </div>
          )}
          
          {/* Auto-redirect countdown */}
          {isPaymentSuccessful && (
            <div className="mt-6 flex flex-col items-center">
              <p className="text-sm text-gray-500 mb-3">
                Redirecting to homepage in <span className="font-bold text-green-600">{redirectCountdown}</span> seconds...
              </p>
              <Button 
                onClick={() => navigate('/')}
                className="bg-green-600 hover:bg-green-700"
              >
                <Home className="w-4 h-4 mr-2" />
                Go to Homepage Now
              </Button>
            </div>
          )}
        </div>

        {/* Order Details */}
        {isPaymentSuccessful && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" ref={receiptRef}>
            {/* Order Summary */}
            <div className="lg:col-span-2 space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Package className="w-5 h-5 mr-2 text-green-600" />
                    Order Summary
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* Order Items */}
                    {order?.items?.length > 0 && (
                      <div className="border-b pb-4 mb-4">
                        <h4 className="font-medium text-gray-700 mb-3">Items Ordered</h4>
                        <div className="space-y-3">
                          {order.items.map((item, index) => (
                            <div key={index} className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                {item.image && (
                                  <img src={item.image} alt={item.name} className="w-12 h-12 object-cover rounded" />
                                )}
                                <div>
                                  <p className="font-medium text-gray-900">{item.name}</p>
                                  <p className="text-sm text-gray-500">Qty: {item.quantity}</p>
                                </div>
                              </div>
                              <span className="font-medium">{formatPrice(item.price * item.quantity)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Order Status */}
                    <div className="flex items-center justify-between py-3 border-b">
                      <span className="font-medium">Order Status</span>
                      <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium capitalize">
                        {order?.order_status || 'Pending'}
                      </span>
                    </div>
                    
                    {/* Payment Method */}
                    <div className="flex items-center justify-between py-3 border-b">
                      <span className="font-medium">Payment Method</span>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        isCOD ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'
                      }`}>
                        {isCOD ? 'Cash on Delivery' : 'Paid Online'}
                      </span>
                    </div>
                    
                    {/* Payment Status */}
                    <div className="flex items-center justify-between py-3 border-b">
                      <span className="font-medium">Payment Status</span>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        isCOD ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'
                      }`}>
                        {isCOD ? 'Pay on Delivery' : 'Paid'}
                      </span>
                    </div>
                    
                    {/* Amount */}
                    <div className="flex items-center justify-between py-3 border-b">
                      <span className="font-medium">{isCOD ? 'Amount to Pay' : 'Amount Paid'}</span>
                      <span className="text-2xl font-bold text-green-600">
                        {formatPrice(displayAmount)}
                      </span>
                    </div>
                    
                    {/* Transaction/Order ID */}
                    <div className="flex items-center justify-between py-3">
                      <span className="font-medium">Order ID</span>
                      <span className="text-sm text-gray-500 font-mono bg-gray-100 px-2 py-1 rounded">
                        #{orderId?.slice(-8).toUpperCase() || 'N/A'}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Shipping Address */}
              {order?.shipping_address && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center text-lg">
                      <MapPin className="w-5 h-5 mr-2 text-blue-600" />
                      Shipping Address
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-gray-700">
                      <p className="font-medium">{order.shipping_address.name || order.guest_name}</p>
                      <p>{order.shipping_address.street}</p>
                      {order.shipping_address.apartment && <p>{order.shipping_address.apartment}</p>}
                      <p>{order.shipping_address.city}, {order.shipping_address.state} - {order.shipping_address.postal_code}</p>
                      {order.shipping_address.phone && (
                        <p className="mt-2 flex items-center gap-2">
                          <Phone className="w-4 h-4" />
                          {order.shipping_address.phone}
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* What happens next */}
              <Card>
                <CardHeader>
                  <CardTitle>What happens next?</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Mail className="w-4 h-4 text-green-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">
                          Order Confirmation SMS
                        </h4>
                        <p className="text-gray-600 text-sm">
                          You'll receive an SMS confirmation with your order ID and tracking details.
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-4">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Package className="w-4 h-4 text-blue-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">
                          Order Processing
                        </h4>
                        <p className="text-gray-600 text-sm">
                          The vendor will pack your order and prepare it for shipment within 1-2 business days.
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-4">
                      <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Truck className="w-4 h-4 text-purple-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">
                          Shipping Updates
                        </h4>
                        <p className="text-gray-600 text-sm">
                          You'll receive SMS updates with tracking information once your order ships.
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Quick Actions Sidebar */}
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Quick Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Link to="/orders" className="block">
                    <Button className="w-full justify-start bg-green-600 hover:bg-green-700" data-testid="view-orders-button">
                      <Package className="w-4 h-4 mr-2" />
                      View My Orders
                    </Button>
                  </Link>
                  
                  <Button 
                    variant="outline" 
                    className="w-full justify-start"
                    onClick={handlePrintReceipt}
                    data-testid="print-receipt-button"
                  >
                    <Printer className="w-4 h-4 mr-2" />
                    Print Receipt
                  </Button>
                  
                  <Link to="/products" className="block">
                    <Button variant="outline" className="w-full justify-start">
                      Continue Shopping
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>

              {/* Support Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Need Help?</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 text-sm mb-4">
                    If you have any questions about your order, our support team is here to help.
                  </p>
                  <div className="space-y-3">
                    <Button variant="outline" size="sm" className="w-full">
                      Contact Support
                    </Button>
                    <Button variant="outline" size="sm" className="w-full">
                      <Phone className="w-4 h-4 mr-2" />
                      WhatsApp Chat
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Back to Home for failed/pending payments */}
        {!isPaymentSuccessful && (
          <div className="text-center mt-8">
            <Link to="/">
              <Button variant="outline">Back to Home</Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
